import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import PageLayout from '@/components/feature/PageLayout';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedSlug, setSelectedSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setSelectedSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === selectedSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === selectedSlug);

  return (
    <PageLayout>
      <title>{`${currentCategory.name} | 十八禁合规指引平台`}</title>
      <meta
        name="description"
        content={currentCategory.description}
      />
      <meta
        name="keywords"
        content={`十八禁${currentCategory.name},${currentCategory.name}知识,十八禁合规,成人内容${currentCategory.name}`}
      />
      <link rel="canonical" href={`https://hyel.com.cn/category/${selectedSlug}`} />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: currentCategory.name },
        ]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
            {currentCategory.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {currentCategory.description}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center justify-center mb-10 overflow-x-auto">
          <div className="inline-flex bg-background-200/70 rounded-full px-1 py-1 gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedSlug(cat.slug);
                  window.history.pushState(null, '', `/category/${cat.slug}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedSlug === cat.slug
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {categoryArticles.map((article) => (
            <article key={article.slug}>
              <Link
                to={`/article/${article.slug}`}
                className="block p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 transition-all duration-300 hover:border-primary-300 cursor-pointer h-full"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    {article.date}
                  </span>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">
                    {article.readTime}
                  </span>
                </div>
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-2 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed line-clamp-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center flex-wrap gap-1.5 mt-3">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md text-xs text-secondary-600 bg-secondary-100 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            </article>
          ))}
        </div>

        {categoryArticles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-inbox-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无文章</p>
          </div>
        )}
      </section>

      <BackToTop />
    </PageLayout>
  );
}